import type { UpdateAcademyAnnouncementBannerParams } from '../../api';
import { Body, Button, Col, Row, useDialog } from '@workramp/ui';
import { fromAbsolute, getLocalTimeZone, now } from '@internationalized/date';
import { ConfirmModal } from '~/features/common';
import {
  AnnouncementBannerScheduleModal,
  AnnouncementBannerScheduleModalModes,
} from './AnnouncementBannerScheduleModal';

export const AnnouncementBannerVisibilitySettings = ({
  isPublished,
  startTime,
  endTime,
  onChange,
}: {
  isPublished: boolean;
  startTime: number | null;
  endTime: number | null;
  onChange: (data: Omit<UpdateAcademyAnnouncementBannerParams, 'id'>) => void;
}) => {
  const announcementBarVisibilityModal = useDialog(AnnouncementBannerScheduleModal);
  const confirmDisableModal = useDialog(ConfirmModal);

  const handleEdit = (mode: AnnouncementBannerScheduleModalModes) => {
    announcementBarVisibilityModal.open({
      mode,
      onClose: announcementBarVisibilityModal.remove,
      startTime,
      endTime,
      onSave: (data) => {
        onChange(data);
        announcementBarVisibilityModal.remove();
      },
    });
  };

  const handleDisable = () => {
    confirmDisableModal.open({
      title: 'Are you sure you want to disable the current banner?',
      onConfirm: () => {
        onChange({
          startTime: null,
          endTime: null,
        });
        confirmDisableModal.remove();
      },
      confirm: 'Yes, Disable Now',
      onCancel: confirmDisableModal.remove,
    });
  };

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

  const hasPassedEndTime = () => {
    if (!endTime) return false;

    const timezone = getLocalTimeZone();
    const currentTime = now(timezone).set({ second: 0, millisecond: 0 });
    const endtime = fromAbsolute(endTime, timezone);

    return currentTime.compare(endtime) > 0;
  };

  return (
    <>
      {startTime && !hasPassedEndTime() && (
        <Body level="md">
          {formatter.format(new Date(startTime))} - {endTime ? formatter.format(new Date(endTime)) : 'No End Date Set'}
        </Body>
      )}
      <Row gapX={2}>
        {!isPublished && startTime && (
          <>
            {!endTime && (
              <Col xs="auto">
                <Button
                  variant="outline"
                  leadingIconName="calendar:regular"
                  color="secondary"
                  onPress={() => handleEdit(AnnouncementBannerScheduleModalModes.EDIT_SCHEDULE)}
                >
                  Edit Schedule
                </Button>
              </Col>
            )}

            {endTime && (
              <Col xs="auto">
                <Button
                  variant="outline"
                  leadingIconName="calendar:regular"
                  color="secondary"
                  onPress={() => handleEdit(AnnouncementBannerScheduleModalModes.EDIT_ENDTIME)}
                >
                  Edit End Date
                </Button>
              </Col>
            )}
          </>
        )}

        {isPublished &&
          (!endTime ? (
            <Col xs="auto">
              <Button
                variant="outline"
                leadingIconName="calendar:regular"
                color="secondary"
                onPress={() => handleEdit(AnnouncementBannerScheduleModalModes.EDIT_SCHEDULE)}
              >
                Edit Schedule
              </Button>
            </Col>
          ) : (
            <Col xs="auto">
              <Button
                variant="outline"
                leadingIconName="calendar:regular"
                color="secondary"
                onPress={() => handleEdit(AnnouncementBannerScheduleModalModes.EDIT_ENDTIME)}
              >
                Edit End Date
              </Button>
            </Col>
          ))}

        <Col xs="auto">
          {isPublished ? (
            <Button color="secondary" variant="outline" leadingIconName="eye-close:regular" onPress={handleDisable}>
              Disable Now
            </Button>
          ) : (
            <Button
              onPress={() => {
                onChange({
                  startTime: now('UTC').toDate().valueOf(),
                  endTime: null,
                });
              }}
            >
              Enable Now
            </Button>
          )}
        </Col>
        {!isPublished && !startTime && (
          <Col xs="auto">
            <Button
              variant="outline"
              leadingIconName="calendar:regular"
              color="secondary"
              onPress={() => handleEdit(AnnouncementBannerScheduleModalModes.SCHEDULE)}
            >
              Schedule
            </Button>
          </Col>
        )}
      </Row>
    </>
  );
};
